import React, { useEffect, useState } from 'react';

import { DropdownMenuItem } from 'components/GMetrixButtonDropdown/GMetrixButtonDropdown';
import type { TableFilter } from 'components/GMetrixTable/components/toolbar/components';
import { DetailsButtonItems, GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { StudentGroupApi, StudentGroupWithDetails } from 'apis/StudentGroupApi';
import { GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { GroupPermissions } from 'pages/groups/types';
import { useNotification } from 'app/notifications';
import { SortDescriptor } from 'utils/dataQuery';
import { UsersApi } from 'apis/UsersApi';
import { useIntl } from 'react-intl';

import 'components/GroupGMetrixTable/group-gmetrix-table.scss';

interface GroupGMetrixTableProps<T> {
    allowExport?: boolean;
    columnData: GMetrixTableColumnProps<StudentGroupWithDetails>[];
    currentUserId: number;
    defaultItemsPerPage?: number;
    detailsButtonItems?: DetailsButtonItems[];
    detailsDropdownItems?: (dataItem: StudentGroupWithDetails) => DropdownMenuItem[];
    groups: StudentGroupWithDetails[];
    initialSkip?: number;
    loadingGridData: boolean;
    manuallyRenderGroups?: boolean;
    multiSelect?: boolean;
    onDetailsItemClick?: (dataItem: StudentGroupWithDetails) => void | undefined | Promise<void>;
    onExportAllData?: () => Promise<StudentGroupWithDetails[]>;
    onFilterChange?: (filters: { showArchived: boolean; showMyClassrooms: boolean }) => void;
    onSelectedChange?: (selected: StudentGroupWithDetails[]) => void;
    onServerPageChange?: (skip: number, take: number) => void;
    onServerSortChange?: (sort: SortDescriptor[]) => void;
    permissions?: GroupPermissions;
    searchTerm: string;
    selectableRows?: boolean;
    selectedGroups?: StudentGroupWithDetails[];
    serverSidePagination?: boolean;
    serverTotalCount?: number;
    setGroups: (groups: StudentGroupWithDetails[]) => void;
    setLoadingGridData: React.Dispatch<React.SetStateAction<boolean>>;
    setSearchTerm: (searchTerm: string) => void;
    toggleColumns?: boolean;
}

export function GroupGMetrixTable<T>({
    allowExport = true,
    columnData,
    currentUserId,
    defaultItemsPerPage,
    detailsButtonItems,
    detailsDropdownItems,
    groups,
    initialSkip,
    loadingGridData,
    manuallyRenderGroups = false,
    multiSelect = true,
    onDetailsItemClick,
    onExportAllData,
    onFilterChange,
    onSelectedChange,
    onServerPageChange,
    onServerSortChange,
    permissions,
    searchTerm,
    selectableRows = true,
    selectedGroups,
    serverSidePagination = false,
    serverTotalCount,
    setGroups,
    setLoadingGridData,
    setSearchTerm,
    toggleColumns = true
}: GroupGMetrixTableProps<T>) {

    const { formatMessage } = useIntl();
    const { catchWithToast } = useNotification();
    const [groupSwitch, setGroupSwitch] = useState(permissions?.MyClassrooms);
    const [showArchivedClassrooms, setShowArchivedClassrooms] = useState(false);
    const [filteredGroups, setFilteredGroups] = useState<StudentGroupWithDetails[]>([]);

    useEffect(() => {

        const myClassrooms = sessionStorage.getItem('myClassrooms');
        const archivedClassrooms = sessionStorage.getItem('archivedClassrooms');

        let initialMyClassrooms = permissions?.MyClassrooms ?? false;
        let initialShowArchived = false;

        if (myClassrooms) {

            initialMyClassrooms = JSON.parse(myClassrooms);

        }
        if (archivedClassrooms) {

            initialShowArchived = JSON.parse(archivedClassrooms);

        }

        setGroupSwitch(initialMyClassrooms);
        setShowArchivedClassrooms(initialShowArchived);

        if (serverSidePagination && onFilterChange) {

            onFilterChange({ showArchived: initialShowArchived, showMyClassrooms: initialMyClassrooms });

        }

        if (!manuallyRenderGroups && !serverSidePagination) {

            setLoadingGridData(true);
            StudentGroupApi.GetAll('Partner')
                .then(res => {

                    setGroups(res);

                })
                .catch((error: unknown) => catchWithToast(error, formatMessage({ id: 'failed_to_load_classrooms.afc12d1d', defaultMessage: 'Failed to load classrooms.' })))
                .finally(() => setLoadingGridData(false));

        }

    }, []);

    useEffect(() => {

        if (serverSidePagination) {

            return;

        }
        const noDuplicateGroups = groups.filter((v, i, a) => a.findIndex(v2 => v2.Id === v.Id && v2.Id === v.Id) === i);
        let userGroupList: any[] = noDuplicateGroups;
        if (!showArchivedClassrooms) {

            userGroupList = userGroupList.filter(group => group.Archived === false);

        }
        if (groupSwitch) {

            userGroupList = userGroupList.filter(group => {

                if (group.Instructors) {

                    const currentUserIsAnInstructor = group.Instructors.find(
                        (instructor: any) => instructor.Id === currentUserId
                    );
                    if (group.UserId === currentUserId || currentUserIsAnInstructor) {

                        return group;

                    }

                }

            });

        }
        setFilteredGroups(userGroupList);

    }, [groupSwitch, groups, showArchivedClassrooms]);

    const handleArchivedChange = (checked: boolean) => {

        setShowArchivedClassrooms(checked);
        sessionStorage.setItem('archivedClassrooms', JSON.stringify(checked));
        if (serverSidePagination && onFilterChange) {

            onFilterChange({ showArchived: checked, showMyClassrooms: groupSwitch ?? false });

        }

    };

    const handleMyClassroomsChange = (checked: boolean) => {

        setGroupSwitch(checked);
        sessionStorage.setItem('myClassrooms', JSON.stringify(checked));
        UsersApi.SetPagePreference(
            'http://schemas.gmetrix.net/claims/Admin/Preference/PagePreference/MyClassrooms',
            checked ? 'Show' : 'Hide'
        );
        if (serverSidePagination && onFilterChange) {

            onFilterChange({ showArchived: showArchivedClassrooms, showMyClassrooms: checked });

        }

    };

    const filters: TableFilter[] = [
        { checked: showArchivedClassrooms, label: formatMessage({ id: 'show_archived_classrooms.aaac7a91', defaultMessage: 'Show Archived Classrooms' }), onSelect: handleArchivedChange, type: 'checkbox' },
        { checked: groupSwitch ?? false, label: formatMessage({ id: 'show_only_my_classrooms.98a852d2', defaultMessage: 'Show Only My Classrooms' }), onSelect: handleMyClassroomsChange, type: 'checkbox' }
    ];

    return (
        <>
            <GMetrixTable
                allowExport={allowExport}
                bottomBarTableName={formatMessage({ id: 'classrooms.e66138fb', defaultMessage: 'Classrooms' })}
                columnData={columnData}
                defaultItemsPerPage={defaultItemsPerPage}
                detailsButtonItems={detailsButtonItems}
                detailsDropdownItems={detailsDropdownItems}
                excelExportFileName={formatMessage({ id: 'classroomlist.688cbcca', defaultMessage: 'ClassroomList' })}
                filters={filters}
                initialSkip={initialSkip}
                loading={loadingGridData}
                multiSelect={multiSelect}
                noDataMessage={formatMessage({ id: 'search_for_a_classroom_to_begin_using.8b6a9b30', defaultMessage: 'Search for a classroom to begin using' })}
                onDetailsItemClick={onDetailsItemClick}
                onExportAllData={onExportAllData}
                onSelectedChange={onSelectedChange}
                onServerPageChange={serverSidePagination ? onServerPageChange : undefined}
                onServerSortChange={serverSidePagination ? onServerSortChange : undefined}
                resetPageSignal={`archived:${showArchivedClassrooms}|mine:${groupSwitch}`}
                rowData={serverSidePagination ? groups : filteredGroups}
                searchPlaceholder={formatMessage({ id: 'search_by_name_or_identifier.c40c23cb', defaultMessage: 'Search by name or identifier...' })}
                searchTerm={searchTerm}
                selectableRows={selectableRows}
                selected={selectedGroups}
                serverSidePagination={serverSidePagination}
                serverTotalCount={serverTotalCount}
                setSearchTerm={setSearchTerm}
                tableName={formatMessage({ id: 'classrooms.e66138fb', defaultMessage: 'Classrooms' })}
                toggleColumns={toggleColumns}
            />
        </>
    );

}
